import { memo, useEffect, useId, useMemo, useState } from 'react';
import { useTranslations } from '@/i18n';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar } from '@/components/ui/calendar';
import { Spinner } from '@/components/ui/shadcn-io/spinner';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { endOfDay, format, isAfter, isBefore, startOfDay } from 'date-fns';
import { ka } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { useHotelDailyAmount } from '@/hooks/trips/useHotelDailyAmount';

export interface AccommodationData {
  checkInDate: Date;
  checkOutDate: Date;
  totalAmount: string;
  currency: string;
  needsBookingHelp: boolean;
}

interface AccommodationExpenseFormProps {
  onSave: (data: AccommodationData) => void | Promise<void>;
  onCancel: () => void;
  initialData?: AccommodationData;
  isLoading?: boolean;
  errorMessage?: string | null;
  defaultCheckInDate?: Date | null;
  defaultCheckOutDate?: Date | null;
  allowedCheckInDate?: Date | null;
  allowedCheckOutDate?: Date | null;
  tripId: string;
  /** Whether the initial dates are outside the allowed city date range */
  hasDateIssue?: boolean;
  /** Whether same-day check-in/check-out is allowed (for single-day connected cities) */
  allowSameDayCheckout?: boolean;
}

function AccommodationExpenseFormComponent({
  onSave,
  onCancel,
  initialData,
  isLoading,
  errorMessage,
  defaultCheckInDate,
  defaultCheckOutDate,
  allowedCheckInDate,
  allowedCheckOutDate,
  tripId,
  hasDateIssue = false,
  allowSameDayCheckout = false,
}: AccommodationExpenseFormProps) {
  const t = useTranslations('expenses');

  // Local state for the form
  const [checkInDate, setCheckInDate] = useState<Date | undefined>(
    initialData?.checkInDate ?? defaultCheckInDate ?? undefined
  );
  const [checkOutDate, setCheckOutDate] = useState<Date | undefined>(
    initialData?.checkOutDate ?? defaultCheckOutDate ?? undefined
  );
  const initialTotalAmount = initialData?.totalAmount ?? '';
  const [totalAmount, setTotalAmount] = useState(initialTotalAmount);
  const [needsBookingHelp, setNeedsBookingHelp] = useState(initialData?.needsBookingHelp || false);
  const [amountError, setAmountError] = useState<string | null>(null);
  const [isCheckInDateOpen, setIsCheckInDateOpen] = useState(false);
  const [isCheckOutDateOpen, setIsCheckOutDateOpen] = useState(false);
  const bookingHelpCheckboxId = useId();
  const businessTripId = useMemo(() => Number.parseInt(tripId, 10), [tripId]);
  const hasBusinessTripId = Number.isFinite(businessTripId);
  const effectiveAllowedCheckIn = useMemo(
    () => allowedCheckInDate ?? defaultCheckInDate ?? null,
    [allowedCheckInDate, defaultCheckInDate]
  );
  const effectiveAllowedCheckOut = useMemo(
    () => allowedCheckOutDate ?? defaultCheckOutDate ?? null,
    [allowedCheckOutDate, defaultCheckOutDate]
  );
  const normalizedAllowedCheckIn = useMemo(
    () => (effectiveAllowedCheckIn ? startOfDay(effectiveAllowedCheckIn) : undefined),
    [effectiveAllowedCheckIn]
  );
  const normalizedAllowedCheckOut = useMemo(
    () => (effectiveAllowedCheckOut ? endOfDay(effectiveAllowedCheckOut) : undefined),
    [effectiveAllowedCheckOut]
  );
  const normalizedCheckIn = useMemo(
    () => (checkInDate ? startOfDay(checkInDate) : undefined),
    [checkInDate]
  );
  const checkInDisabledMatcher = useMemo(() => {
    if (!normalizedAllowedCheckIn && !normalizedAllowedCheckOut) {
      return undefined;
    }

    return (date: Date) => {
      const day = startOfDay(date);
      if (normalizedAllowedCheckIn && isBefore(day, normalizedAllowedCheckIn)) {
        return true;
      }
      if (normalizedAllowedCheckOut && isAfter(day, normalizedAllowedCheckOut)) {
        return true;
      }
      return false;
    };
  }, [normalizedAllowedCheckIn, normalizedAllowedCheckOut]);
  const checkOutDisabledMatcher = useMemo(() => {
    if (!normalizedAllowedCheckIn && !normalizedAllowedCheckOut && !normalizedCheckIn) {
      return undefined;
    }

    return (date: Date) => {
      const day = startOfDay(date);
      if (normalizedAllowedCheckIn && isBefore(day, normalizedAllowedCheckIn)) {
        return true;
      }
      if (normalizedAllowedCheckOut && isAfter(day, normalizedAllowedCheckOut)) {
        return true;
      }
      if (normalizedCheckIn) {
        // For same-day checkout (single-day connected cities), allow checkout on check-in day
        // Otherwise, checkout must be after check-in
        if (allowSameDayCheckout) {
          if (isBefore(day, normalizedCheckIn)) {
            return true;
          }
        } else {
          if (!isAfter(day, normalizedCheckIn)) {
            return true;
          }
        }
      }
      return false;
    };
  }, [
    normalizedAllowedCheckIn,
    normalizedAllowedCheckOut,
    normalizedCheckIn,
    allowSameDayCheckout,
  ]);
  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat('ka-GE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    []
  );
  const checkInDateString = checkInDate ? format(checkInDate, 'yyyy-MM-dd') : null;
  const checkOutDateString = checkOutDate ? format(checkOutDate, 'yyyy-MM-dd') : null;
  const isDateRangeValid = Boolean(
    checkInDate &&
      checkOutDate &&
      (allowSameDayCheckout ? checkOutDate >= checkInDate : checkOutDate > checkInDate)
  );
  const shouldFetchAmount = Boolean(hasBusinessTripId && isDateRangeValid && !needsBookingHelp);
  const hasAmount = totalAmount !== '' && !Number.isNaN(Number(totalAmount));
  const formattedTotalAmount = hasAmount
    ? `₾ ${currencyFormatter.format(Number(totalAmount))}`
    : '';
  const displayTotalAmount = formattedTotalAmount;
  const amountTextClass = hasAmount ? 'text-[#292929]' : 'text-[#bbbbbb]';
  const {
    data: hotelQuote,
    isFetching: isHotelQuoteFetching,
    isError: isHotelQuoteError,
    error: hotelQuoteError,
  } = useHotelDailyAmount({
    tripId: hasBusinessTripId ? businessTripId : undefined,
    checkInDate: checkInDateString,
    checkOutDate: checkOutDateString,
    enabled: shouldFetchAmount,
  });
  const isSaveDisabled = isLoading || isHotelQuoteFetching || !totalAmount || Boolean(amountError);
  const showSaveSpinner = Boolean(isLoading || isHotelQuoteFetching);

  useEffect(() => {
    if (initialData) {
      return;
    }

    if (!checkInDate && defaultCheckInDate) {
      setCheckInDate(defaultCheckInDate);
    }
  }, [checkInDate, defaultCheckInDate, initialData]);

  useEffect(() => {
    if (initialData) {
      return;
    }

    if (!checkOutDate && defaultCheckOutDate) {
      setCheckOutDate(defaultCheckOutDate);
    }
  }, [checkOutDate, defaultCheckOutDate, initialData]);

  useEffect(() => {
    if (needsBookingHelp) {
      setAmountError(null);
      setTotalAmount('0');
      return;
    }

    if (!shouldFetchAmount || !hasBusinessTripId) {
      setAmountError(null);
      setTotalAmount(initialTotalAmount);
      return;
    }

    if (isHotelQuoteFetching) {
      setAmountError(null);
      setTotalAmount('');
    }
  }, [
    needsBookingHelp,
    shouldFetchAmount,
    hasBusinessTripId,
    isHotelQuoteFetching,
    initialTotalAmount,
  ]);

  useEffect(() => {
    if (hotelQuote && shouldFetchAmount && !needsBookingHelp) {
      const total = Number(hotelQuote.total_daily_amount ?? 0);
      setTotalAmount(Number.isFinite(total) ? total.toString() : '');
      setAmountError(null);
    }
  }, [hotelQuote, shouldFetchAmount, needsBookingHelp]);

  useEffect(() => {
    if (isHotelQuoteError && !needsBookingHelp) {
      setAmountError(hotelQuoteError?.message ?? t('error'));
      setTotalAmount('');
    }
  }, [isHotelQuoteError, hotelQuoteError, needsBookingHelp, t]);

  const handleSave = async () => {
    if (checkInDate && checkOutDate && totalAmount && !amountError && !isHotelQuoteFetching) {
      await onSave({
        checkInDate,
        checkOutDate,
        totalAmount,
        currency: 'GEL',
        needsBookingHelp,
      });
    }
  };

  return (
    <div className="flex w-full flex-col gap-4 rounded-2xl border border-[#ebebeb] bg-white p-5">
      {/* Header with title and action buttons */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex w-full items-center gap-3 sm:w-auto">
          <div className="flex items-center justify-center rounded-[2rem] border border-[#f1f1f1] bg-[#f9f9f9] p-2">
            <img src="/assets/icons/building.svg" alt="" className="h-6 w-6" />
          </div>
          <span className="text-[1.125rem] leading-6 font-medium text-[#292929]">
            {t('accommodation')}
          </span>
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:gap-2">
          <Button
            type="button"
            onClick={onCancel}
            variant="unstyled"
            size="none"
            className="w-full rounded-[1.875rem] bg-white px-3.5 py-2.5 text-sm leading-5 font-medium text-black hover:bg-[#f9f9f9] sm:w-auto"
          >
            {t('cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={isSaveDisabled}
            variant="unstyled"
            size="none"
            className="flex w-full items-center justify-center gap-2 rounded-[1.875rem] bg-[#242424] px-3.5 py-2.5 text-sm leading-5 font-medium text-white hover:bg-[#333333] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
          >
            {showSaveSpinner && <Spinner variant="circle" className="h-4 w-4" />}
            {t('save')}
          </Button>
        </div>
      </div>

      {/* Border line */}
      <div className="h-0 w-full border-t border-[#f0f0f0]" />

      {errorMessage && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
          {errorMessage}
        </div>
      )}

      {/* Booking help checkbox */}
      <div className="flex min-h-[2.75rem] items-center gap-2">
        <Checkbox
          id={bookingHelpCheckboxId}
          checked={needsBookingHelp}
          onCheckedChange={(checked) => {
            const isChecked = checked === true;
            setNeedsBookingHelp(isChecked);
            if (isChecked) {
              setAmountError(null);
              setTotalAmount('0');
            }
          }}
          className="h-5 w-5"
        />
        <label
          htmlFor={bookingHelpCheckboxId}
          className="cursor-pointer text-[0.9375rem] leading-6 font-normal text-[#292929]"
        >
          {t('needsBookingHelp')}
        </label>
      </div>

      {/* Date fields */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {/* Check-in Date */}
        <div className="flex-1">
          <div className="px-1 pb-3">
            <label className="text-sm font-medium text-[#292929]">{t('checkInDate')}</label>
          </div>
          <Popover open={isCheckInDateOpen} onOpenChange={setIsCheckInDateOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  'h-14 w-full justify-between rounded-2xl border-[#ebebeb] px-4 text-left font-normal',
                  !checkInDate && 'text-muted-foreground',
                  hasDateIssue && 'border-red-500'
                )}
              >
                <div className="flex items-center gap-3">
                  <img src="/assets/icons/calendar.svg" alt="" className="size-6" />
                  <span className={cn('text-base', hasDateIssue && checkInDate && 'text-red-500')}>
                    {checkInDate ? format(checkInDate, 'dd/MM/yyyy') : t('selectDate')}
                  </span>
                </div>
                <img src="/assets/icons/chevron-down.svg" alt="" className="size-6" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={checkInDate}
                onSelect={(date) => {
                  setCheckInDate(date);
                  setIsCheckInDateOpen(false);
                }}
                locale={ka}
                captionLayout="dropdown"
                className="rounded-2xl border-[#ebebeb]"
                defaultMonth={checkInDate ?? effectiveAllowedCheckIn ?? new Date()}
                fromDate={effectiveAllowedCheckIn ?? undefined}
                toDate={effectiveAllowedCheckOut ?? undefined}
                disabled={checkInDisabledMatcher}
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Check-out Date */}
        <div className="flex-1">
          <div className="px-1 pb-3">
            <label className="text-sm font-medium text-[#292929]">{t('checkOutDate')}</label>
          </div>
          <Popover open={isCheckOutDateOpen} onOpenChange={setIsCheckOutDateOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  'h-14 w-full justify-between rounded-2xl border-[#ebebeb] px-4 text-left font-normal',
                  !checkOutDate && 'text-muted-foreground',
                  hasDateIssue && 'border-red-500'
                )}
              >
                <div className="flex items-center gap-3">
                  <img src="/assets/icons/calendar.svg" alt="" className="size-6" />
                  <span className={cn('text-base', hasDateIssue && checkOutDate && 'text-red-500')}>
                    {checkOutDate ? format(checkOutDate, 'dd/MM/yyyy') : t('selectDate')}
                  </span>
                </div>
                <img src="/assets/icons/chevron-down.svg" alt="" className="size-6" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={checkOutDate}
                onSelect={(date) => {
                  setCheckOutDate(date);
                  setIsCheckOutDateOpen(false);
                }}
                locale={ka}
                captionLayout="dropdown"
                className="rounded-2xl border-[#ebebeb]"
                defaultMonth={checkOutDate ?? checkInDate ?? effectiveAllowedCheckIn ?? new Date()}
                fromDate={checkInDate ?? effectiveAllowedCheckIn ?? undefined}
                toDate={effectiveAllowedCheckOut ?? undefined}
                disabled={checkOutDisabledMatcher}
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Total Amount */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between px-1">
          <label className="text-sm font-medium text-[#292929]">{t('totalAmount')}</label>
          {isHotelQuoteFetching && <Spinner variant="circle" className="h-4 w-4 text-[#292929]" />}
        </div>
        <div
          className={cn(
            'flex h-14 items-center rounded-2xl border border-[#e4e4e4] bg-[#f7f7f7] px-4 text-base leading-6 text-[#292929] shadow-none',
            'cursor-default transition-none select-none',
            amountError ? 'border-[#ff4d4f]' : ''
          )}
        >
          <span className={cn('inline-flex min-h-[1.5rem] items-center', amountTextClass)}>
            {displayTotalAmount}
          </span>
        </div>
        {amountError && <p className="px-1 text-sm leading-5 text-[#ff4d4f]">{amountError}</p>}
      </div>
    </div>
  );
}

const AccommodationExpenseForm = memo(AccommodationExpenseFormComponent);

export default AccommodationExpenseForm;
